import { type FC, useEffect, useLayoutEffect, useMemo, useState } from "react"
import { Navigate, useNavigate, useParams } from "react-router"
import styled from "styled-components"

import { type VideoCaptionLanguageCode, type VideoTag, type VideoWithCaptions } from "@forento/core/models/video"
import { parseNumber } from "@forento/core/utilities/number"
import Tooltip from "@forento/ui/components/Tooltip"
import { useFile } from "@forento/ui/hooks/useFile"
import { toggleStateArray } from "@forento/ui/utilities/array"

import Button from "~/components/Button"
import { SettingsIcon } from "~/components/Icon"
import ImageUploader from "~/components/ImageUploader"
import InputField, { useRichTextArea } from "~/components/InputField"
import Layout, { PageBreadcrumb, PageHeader, PageHeaderGroup } from "~/components/Layout"
import ManageTagsModal from "~/components/ManageTagsModal"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import ProductPricing from "~/components/ProductPricing"
import RichContentEditor from "~/components/RichContentEditor"
import SelectTags from "~/components/SelectTags"
import VideoCaptions from "~/components/VideoCaptions"
import ZapierId from "~/components/ZapierId"
import { useAlert } from "~/contexts/AlertContext"
import { useToast } from "~/contexts/ToastContext"
import usePrice from "~/hooks/usePrice"
import { arrayBufferToBase64 } from "~/utilities/buffer"
import {
	exportEditedRichContent,
	loadRichContent,
	parseRichContent,
	type RichContentValue,
} from "~/utilities/richContent"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

const EditVideoPage: FC = () => {
	const videoId = parseNumber(useParams().videoId) ?? -1
	const alert = useAlert()
	const navigate = useNavigate()

	const video = query.video.get.useQuery({ videoId })
	const tags = query.video.listTags.useQuery()
	const memberships = query.membership.list.useQuery()

	if (video.data === null) return <Navigate to={routes.video.index()} />

	async function handleDelete() {
		const dialog = await alert.confirm(
			"Delete video",
			"Are you sure you want to delete this video? This action is irreversible.",
		)
		if (!dialog.result) return

		try {
			await trpc.video.delete.mutate({ videoId })
			dialog.close()
			navigate(routes.video.index())
		} catch (error) {
			console.error(error)
			await alert.show("Error", "Failed to delete video. Please try again later.")
		}
	}

	return (
		<Layout>
			<PageHeader>
				<PageBreadcrumb title="Edit" path={[{ title: "Videos", link: routes.video.index() }]} />
				<PageHeaderGroup className="items-center">
					<ZapierId id={videoId} />
					<Button variant="primary-danger" onClick={handleDelete}>
						Delete
					</Button>
				</PageHeaderGroup>
			</PageHeader>

			{video.error || tags.error || memberships.error ? (
				<p>Failed to load data.</p>
			) : !video.data || !tags.data || !memberships.data ? (
				<PartialLoadingPage />
			) : (
				<Content video={video.data} tags={tags.data} reloadVideo={video.refetch} reloadTags={tags.refetch} />
			)}
		</Layout>
	)
}

type ContentProps = {
	video: VideoWithCaptions
	tags: VideoTag[]
	reloadVideo(): Promise<unknown>
	reloadTags(): Promise<unknown>
}
const Content: FC<ContentProps> = ({ video, tags, reloadVideo, reloadTags }) => {
	const alert = useAlert()
	const toast = useToast()

	const [title, setTitle] = useState(video.name)
	const [shortDescription, setShortDescription] = useState(video.shortDescription ?? "")
	const longDescription = useRichTextArea({ label: "Description", initialValue: video.longDescription })
	const [presentation, setPresentation] = useState<RichContentValue | null>(loadRichContent(video.presentation))
	const [isSignInRequired, setSignInRequired] = useState(video.isSignInRequired)
	const [isBokaderoMemberRequired, setBokaderoMemberRequired] = useState(video.isBokaderoMemberRequired ?? false)
	const { price, priceValue, setPrice } = usePrice(video.price?.amount)
	const [isManualOnly, setManualOnly] = useState(video.isManualOnly)
	const [membershipIds, setMembershipIds] = useState(video.membershipIds)
	const [isPaused, setPaused] = useState(video.pauseDate !== null)
	const [selectedTags, setSelectedTags] = useState(video.tags.map(x => x.id))
	const [addCaptions, setAddCaptions] = useState<{ language: VideoCaptionLanguageCode; file: File }[]>([])
	const [removeCaptions, setRemoveCaptions] = useState<VideoCaptionLanguageCode[]>([])

	const [isManageTagsModalOpen, setManageTagsModalOpen] = useState(false)

	const thumbnail = useFile(video.id, video.host === "bunny" ? video.thumbnailUrl : null)

	useLayoutEffect(() => {
		const abortController = new AbortController()

		setTitle(video.name)
		setShortDescription(video.shortDescription ?? "")
		setPresentation(loadRichContent(video.presentation))
		setSignInRequired(video.isSignInRequired)
		setPrice(video.price?.amount.toString() ?? "")
		setManualOnly(video.isManualOnly)
		setBokaderoMemberRequired(video.isBokaderoMemberRequired ?? false)
		setMembershipIds(video.membershipIds)
		setPaused(video.pauseDate !== null)
		setSelectedTags(video.tags.map(x => x.id))
		setAddCaptions([])
		setRemoveCaptions([])

		return () => {
			abortController.abort()
		}
	}, [setPrice, video])

	useEffect(() => {
		const isModified =
			title.trim() !== video.name.trim() ||
			shortDescription.trim() !== (video.shortDescription ?? "").trim() ||
			longDescription.isModified ||
			presentation?.isModified ||
			thumbnail.isModified ||
			isSignInRequired !== video.isSignInRequired ||
			priceValue !== (video.price?.amount ?? null) ||
			isManualOnly !== video.isManualOnly ||
			isBokaderoMemberRequired !== (video.isBokaderoMemberRequired ?? false) ||
			membershipIds.toSorted().join(",") !== video.membershipIds.toSorted().join(",") ||
			isPaused !== (video.pauseDate !== null) ||
			selectedTags.toSorted().join(",") !==
				video.tags
					.map(x => x.id)
					.toSorted()
					.join(",") ||
			addCaptions.length > 0 ||
			removeCaptions.length > 0

		if (isModified) {
			toast.setUnsavedChanges(async () => {
				if (title.trim().length === 0) return false

				try {
					const { captionErrors } = await trpc.video.update.mutate({
						videoId: video.id,
						data: {
							title,
							shortDescription,
							longDescription: await longDescription.exportEditorState(),
							presentation: exportEditedRichContent(presentation),
							thumbnailDataUrl: thumbnail.isModified ? await thumbnail.exportToDataUrl() : undefined,
							isSignInRequired,
							price: priceValue,
							isManualOnly,
							isBokaderoMemberRequired,
							membershipIds,
							isPaused,
							tags: selectedTags,
							removeCaptions,
							addCaptions: await Promise.all(
								addCaptions.map(async caption => ({
									language: caption.language,
									base64: arrayBufferToBase64(await caption.file.arrayBuffer()),
								})),
							),
						},
					})
					await reloadVideo()
					if (captionErrors.length > 0) {
						await alert.show(
							"Invalid captions",
							`Your uploaded captions are invalid.\n\n${captionErrors.map(x => `- ${x}`).join("\n")}`,
						)
						return false
					}
					return true
				} catch (error) {
					console.error(error)
					await alert.show("Error", "Failed to save changes. Please try again later.")
					return false
				}
			})
		} else {
			toast.clearUnsavedChanges()
		}
	}, [
		addCaptions,
		alert,
		isBokaderoMemberRequired,
		isManualOnly,
		isSignInRequired,
		longDescription,
		presentation,
		thumbnail,
		membershipIds,
		priceValue,
		isPaused,
		reloadVideo,
		removeCaptions,
		selectedTags,
		shortDescription,
		title,
		toast,
		video,
	])

	const initialPresentation = useMemo(() => parseRichContent(video.presentation), [video.presentation])

	return (
		<Container>
			<ManageTagsModal
				type="video"
				isOpen={isManageTagsModalOpen}
				tags={tags}
				onClose={() => setManageTagsModalOpen(false)}
				onChange={reloadTags}
			/>

			<Section>
				<Subtitle>Basic information</Subtitle>
				<InputField placeholder="Title" value={title} onChange={setTitle} />
				<InputField
					label="Short description"
					placeholder="Short description"
					value={shortDescription}
					onChange={setShortDescription}
				/>
				{longDescription.element}
				<ImageUploader disableSvg label="Thumbnail" recommendedSize="thumbnail" {...thumbnail.imageUploader} />
			</Section>

			<Section>
				<Subtitle>Pricing</Subtitle>
				<ProductPricing
					type="video"
					isSignInRequired={isSignInRequired}
					setSignInRequired={setSignInRequired}
					price={price}
					setPrice={setPrice}
					isManualOnly={isManualOnly}
					setManualOnly={setManualOnly}
					isBokaderoMemberRequired={isBokaderoMemberRequired}
					setBokaderoMemberRequired={setBokaderoMemberRequired}
					membershipIds={membershipIds}
					setMembershipIds={setMembershipIds}
					isPaused={isPaused}
					setPaused={setPaused}
				/>
			</Section>

			<Section>
				<div>
					<Header>
						<Subtitle>Tags</Subtitle>
						<Tooltip tooltip="Manage tags">
							<button
								type="button"
								onClick={() => setManageTagsModalOpen(true)}
								className="cursor-pointer"
							>
								<SettingsIcon className="size-6" />
							</button>
						</Tooltip>
					</Header>
					<Description>Tags help students find your video when they search for specific topics.</Description>
				</div>
				<SelectTags
					tags={tags}
					selectedTags={selectedTags}
					onChange={tagId => toggleStateArray(setSelectedTags, tagId)}
				/>
			</Section>

			<Section>
				<Subtitle>Captions</Subtitle>
				{video.captions ? (
					<VideoCaptions
						captions={video.captions}
						addCaptions={addCaptions}
						removeCaptions={removeCaptions}
						setAddCaptions={setAddCaptions}
						setRemoveCaptions={setRemoveCaptions}
					/>
				) : (
					<p>Captions are only available to newer videos. Upload a new video to add captions.</p>
				)}
			</Section>

			<Section>
				<Subtitle>Presentation</Subtitle>
				<RichContentEditor
					placeholder="Enter video presentation here..."
					initialValue={initialPresentation}
					setValue={setPresentation}
				/>
			</Section>
		</Container>
	)
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding-bottom: 128px;
`

const Section = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`

const Header = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 24px;
	margin-bottom: 10px;
`

const Subtitle = styled.h2`
	font-weight: 600;
	font-size: 24px;
	color: black;
`

const Description = styled.p`
	color: black;
`

export default EditVideoPage
